'use client'
import * as React from 'react'
import { VoteButton } from '@web/app/[locale]/clubhouse/[slug]/_components/action-buttons/vote-button'
import { useMutation } from '@tanstack/react-query'
import type { Project } from '../../page-client'
import {
  useSocialFeedPostAddLikeMutation,
  useSocialFeedPostRemoveLikeMutation,
} from '@web/gen/feed-post'
import { useProject } from '../../_hooks/use-project-data'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'

interface Props {
  project: Project
  size?: 'small' | 'regular'
}

export const ProjectVoteButton = ({ size, project }: Props) => {
  const isLoggedInQuery = useIsLoggedInQuery()
  const { openModal } = useUserAuthModal()
  const { refetch } = useProject({ postSlug: project?.postSlug || '' })
  const socialFeedPostAddLikeMutation = useSocialFeedPostAddLikeMutation()
  const socialFeedPostRemoveLikeMutation = useSocialFeedPostRemoveLikeMutation()

  const { mutate: toggleLike, isPending } = useMutation({
    // oxlint-disable-next-line require-await
    mutationFn: async (currentlyLiked: boolean) => {
      return currentlyLiked
        ? socialFeedPostRemoveLikeMutation.mutateAsync({ input: { postId: Number(project?.id) } })
        : socialFeedPostAddLikeMutation.mutateAsync({ input: { postId: Number(project?.id) } })
    },
    onSuccess: () => {
      refetch()
    },
  })

  const handleLikeClick = React.useCallback(() => {
    if (!isLoggedInQuery.isPending && !isLoggedInQuery.data) {
      openModal('register')
      return
    }
    toggleLike(project?.isLiked)
  }, [project?.isLiked, toggleLike, isLoggedInQuery.isPending, isLoggedInQuery.data, openModal])

  return (
    <VoteButton
      size={size}
      isLiked={project?.isLiked}
      upvotes={project?.likesCount}
      onClick={handleLikeClick}
      isActionLoading={isPending}
    />
  )
}
